import { usePipelineContext } from '@/context/pipelineContext';
import { useLazyBatchGetClientsQuery, useLazyListClientsQuery } from '@/features/clients/clientsApi';
import { clientMatchesDeal, clientMatchesGenericId } from '@/features/clients/helper';
import {
	useLazyBatchGetDealListPoliciesQuery,
	useLazyBatchGetDealsQuery,
	useLazyListDealsQuery,
} from '@/features/deals/dealsApi';
import { getDealClientId } from '@/features/deals/helper';
import { useLazyListTasksQuery } from '@/features/engagements/engagementsApi';
import {
	useGetBrokerGroupsQuery,
	useGetBrokerGroupUsersQuery,
	useGetUserDetailsQuery,
} from '@/features/user/userApi';
import { useGetFormConfigQuery } from '@/features/config/configApi';
import { useGridApiContext } from '@mui/x-data-grid';
import React, { useMemo } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import { useSelector } from 'react-redux';
import { parseValueForCsv } from './data';
import { toTitleCase } from '@/utils/constants';
import { useInstantSearchDataGridContext } from '.';
import { ExportCsvButton } from '@/components/tables/export/ExportCsvButton';
import { downloadCsv } from '@/components/tables/export/downloadCsv';

export const GridToolbarExportButton = () => {
	const apiRef = useGridApiContext();

	const { includePolicies, includeTransactions, title } = useInstantSearchDataGridContext();

	const { uiState, indexUiState, results } = useInstantSearch();
	const indexName = Object.keys(uiState)?.[0];

	const isClientView = indexName === 'clients';
	const isTaskView = indexName === 'tasks';
	const isDealView = indexName === 'deals';

	const filters = indexUiState.configure?.filters;
	const columns = useMemo(() => apiRef.current.getVisibleColumns(), [apiRef.current]);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const userId = userDetails?.userId;
	const brokerGroupArray = userDetails?.brokerGroupArray ?? [];

	const { data: brokerGroups } = useGetBrokerGroupsQuery(
		{ ids: brokerGroupArray, showAdmin: false },
		{
			skip: !brokerGroupArray.length,
		},
	);

	const brokerGroupUsersQuery = useGetBrokerGroupUsersQuery(
		{ userId, showGroup: true },
		{
			skip: !userId,
		},
	);

	const brokerGroupUsers = (brokerGroupUsersQuery.data ?? []).reduce((acc, current) => {
		if (!acc.find((item) => item.id === current.id)) {
			acc.push(current);
		}
		return acc;
	}, []);

	const { pipelines, stages } = usePipelineContext();

	const { data: referralFields } = useGetFormConfigQuery({ configType: 'client' });

	const [getClients] = useLazyListClientsQuery();
	const [getDeals] = useLazyListDealsQuery();
	const [getTasks] = useLazyListTasksQuery();
	const [getBatchClients] = useLazyBatchGetClientsQuery();
	const [getBatchDeals] = useLazyBatchGetDealsQuery();

	const [getBatchDealPolicies] = useLazyBatchGetDealListPoliciesQuery();

	const handleExport = async () => {
		const refinementFilters = Object.entries(indexUiState?.refinementList ?? {})
			.filter(([, values]) => values?.length > 0)
			.map(
				([key, values]) =>
					`${key}: [${values
						.map((v) => (typeof v === 'string' ? `\`${v.replace(/`/g, '``')}\`` : v))
						.join(',')}]`,
			)
			.join(' && ');

		const getData = isClientView ? getClients : isTaskView ? getTasks : getDeals;

		const data = await getData({
			filters: [filters, refinementFilters]
				.filter(Boolean)
				.map((f) => `(${f})`)
				.join(' && '),
		}).unwrap();
		const rowData = data.map((r) => ({ ...r }));

		if (includePolicies && indexName === 'deals') {
			const dealIds = Array.from(new Set(data.map((d) => d.dealId)))
				.filter(Boolean)
				.map(String);
			const policyData = await getBatchDealPolicies({
				dealIds,
				includeTransactions,
				waitForRetrieval: true,
			}).unwrap();

			for (const dealId of dealIds) {
				const i = rowData.findIndex((r) => r.dealId === dealId);
				if (i !== -1) {
					rowData[i].policies = policyData.policiesByDealId[dealId] ?? [];
				}
			}
		}

		if (isTaskView) {
			const { clientIds: taskClientIds, dealIds: taskDealIds } = data.reduce(
				(acc, d) => {
					if (!d.clientId && !d.dealId) return acc;
					if (d.dealId) {
						acc.dealIds.push(d.dealId);
					} else if (d.clientId) {
						acc.clientIds.push(d.clientId);
					}
					return acc;
				},
				{ clientIds: [], dealIds: [] },
			);
			const dealIds = Array.from(new Set(taskDealIds.filter(Boolean).map(String)));
			const deals = await getBatchDeals(dealIds).unwrap();

			const clientIds = Array.from(
				new Set(
					[...taskClientIds, ...deals.map((d) => d.clientDocId)]
						.filter(Boolean)
						.map(String),
				),
			);
			const clients = await getBatchClients(clientIds).unwrap();

			rowData.forEach((r, i) => {
				const client = clients.find((c) => clientMatchesGenericId(c, r.clientId));
				const deal = deals.find((d) => d.dealId?.toString() === r.dealId?.toString());
				if (client) {
					rowData[i].clientName = client.name;
					rowData[i].objectType = 'Client Task';
					rowData[i].contactName = client.contactName ?? r.contactName ?? '';
				}
				if (deal) {
					const dealClient = clients.find((c) => clientMatchesDeal(c, deal));
					const resolvedName = dealClient?.name ?? deal.clientName ?? '';
					if (resolvedName) {
						rowData[i].clientName = resolvedName;
					}
					rowData[i].contactName =
						dealClient?.contactName ?? deal.contactName ?? r.contactName ?? '';

					const pipeline = pipelines.find((p) => p.id === deal.pipeline)?.label ?? '';
					rowData[i].objectType = pipeline;
				}
				if (!deal && !client) rowData[i].objectType = '';
			});
		}

		// Fill contact name from the client when the deal doesn't carry it
		if (isDealView) {
			const missingContactIds = Array.from(
				new Set(
					rowData
						.filter((r) => !r.contactName?.toString?.().trim())
						.map((r) => getDealClientId(r))
						.filter(Boolean)
						.map(String),
				),
			);
			if (missingContactIds.length > 0) {
				const clients = await getBatchClients(missingContactIds).unwrap();
				rowData.forEach((r, i) => {
					if (r.contactName?.toString?.().trim()) return;
					const client = clients.find((c) => clientMatchesDeal(c, r));
					if (client?.contactName) {
						rowData[i].contactName = client.contactName;
					}
				});
			}
		}

		downloadCsv({
			columns: columns.map((column) =>
				isTaskView && column.field === 'status' ? { ...column, headerName: 'Status' } : column,
			),
			rows: rowData,
			getValue: (column, row) =>
				parseValueForCsv({
					column,
					brokerGroupUsers,
					brokerGroups,
					pipelines,
					referralFields,
					stages,
					value: row,
				}),
			fileName: toTitleCase(title ?? indexName).replaceAll(' ', '_'),
		});
	};

	return <ExportCsvButton onExport={handleExport} disabled={results.hits.length === 0} />;
};

GridToolbarExportButton.displayName = 'GridToolbarExportButton';

export default GridToolbarExportButton;
